import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiKeysApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft, Key, Copy, Eye, EyeOff, Trash2, Loader2, Crown,
  BookOpen, BarChart3, Clock, Activity, Webhook, Code2, Sparkles
} from 'lucide-react';

const API_BASE = window.location.origin + '/api';

const WEBHOOK_EVENTS = [
  { event: 'form.created', desc: 'Fires when a new form is created in your workspace.' },
  { event: 'form.updated', desc: 'Fires when a form\'s title, settings, or theme are modified.' },
  { event: 'form.deleted', desc: 'Fires when a form is permanently deleted.' },
  { event: 'form.published', desc: 'Fires when a draft form goes live and starts accepting responses.' },
  { event: 'form.unpublished', desc: 'Fires when a live form is taken offline.' },
  { event: 'submission.created', desc: 'Fires when a new response is submitted to any of your forms.' },
  { event: 'submission.updated', desc: 'Fires when a submission is edited or its status changes.' },
  { event: 'submission.deleted', desc: 'Fires when a submission is permanently removed.' },
  { event: 'submission.export.completed', desc: 'Fires when a CSV or JSON export finishes processing.' },
  { event: 'field.created', desc: 'Fires when a new field is added to a form.' },
  { event: 'field.updated', desc: 'Fires when a field\'s label, type, or validation rules change.' },
  { event: 'field.deleted', desc: 'Fires when a field is removed from a form.' },
  { event: 'logic.updated', desc: 'Fires when conditional logic rules on a form are added, changed, or removed.' },
  { event: 'webhook.failed', desc: 'Fires when a webhook delivery fails after all retry attempts. Use this to monitor integration health.' },
];

const ENDPOINTS = [
  { method: 'GET', path: '/v1/forms', desc: 'List all your forms' },
  { method: 'GET', path: '/v1/forms/:id', desc: 'Get a single form' },
  { method: 'GET', path: '/v1/forms/:id/fields', desc: 'List all fields in a form' },
  { method: 'GET', path: '/v1/forms/:id/logic', desc: 'Get conditional logic rules' },
  { method: 'GET', path: '/v1/forms/:id/submissions', desc: 'List submissions (supports ?limit=&offset=)' },
  { method: 'GET', path: '/v1/forms/:id/submissions/:subId', desc: 'Get a single submission' },
  { method: 'GET', path: '/v1/forms/:id/submissions/export', desc: 'Export submissions (?format=csv|json)' },
  { method: 'GET', path: '/v1/forms/:id/webhooks', desc: 'List webhooks' },
  { method: 'POST', path: '/v1/forms', desc: 'Create a new form' },
  { method: 'POST', path: '/v1/forms/:id/duplicate', desc: 'Duplicate a form' },
  { method: 'POST', path: '/v1/forms/:id/fields', desc: 'Add a field to a form' },
  { method: 'POST', path: '/v1/forms/:id/logic', desc: 'Add a logic rule' },
  { method: 'POST', path: '/v1/forms/:id/publish', desc: 'Publish a form' },
  { method: 'POST', path: '/v1/forms/:id/submissions', desc: 'Create a submission programmatically' },
  { method: 'POST', path: '/v1/forms/:id/unpublish', desc: 'Unpublish a form' },
  { method: 'POST', path: '/v1/forms/:id/webhooks', desc: 'Add a webhook' },
  { method: 'PUT', path: '/v1/forms/:id', desc: 'Update a form (full replace)' },
  { method: 'PUT', path: '/v1/forms/:id/fields/order', desc: 'Reorder fields' },
  { method: 'PUT', path: '/v1/forms/:id/logic', desc: 'Replace all logic rules' },
  { method: 'PATCH', path: '/v1/forms/:id', desc: 'Partially update a form' },
  { method: 'PATCH', path: '/v1/forms/:id/fields/:fieldId', desc: 'Update a specific field' },
  { method: 'DELETE', path: '/v1/forms/:id', desc: 'Delete a form' },
  { method: 'DELETE', path: '/v1/forms/:id/fields/:fieldId', desc: 'Delete a field' },
  { method: 'DELETE', path: '/v1/forms/:id/logic/:ruleId', desc: 'Delete a logic rule' },
  { method: 'DELETE', path: '/v1/forms/:id/submissions/:subId', desc: 'Delete a submission' },
  { method: 'DELETE', path: '/v1/forms/:id/webhooks/:webhookId', desc: 'Delete a webhook' },
];

const METHOD_COLORS = {
  GET: 'bg-emerald-100 text-emerald-700',
  POST: 'bg-blue-100 text-blue-700',
  PUT: 'bg-amber-100 text-amber-700',
  PATCH: 'bg-orange-100 text-orange-700',
  DELETE: 'bg-red-100 text-red-700',
};

export default function ApiPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPaid = user?.plan === 'paid';

  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
  const [showKey, setShowKey] = useState(false);

  const fetchApiKeys = useCallback(async () => {
    if (!isPaid) return;
    setLoading(true);
    try {
      const data = await apiKeysApi.list();
      setApiKeys(data);
    } catch (error) { /* not pro */ }
    finally { setLoading(false); }
  }, [isPaid]);

  useEffect(() => { fetchApiKeys(); }, [fetchApiKeys]);

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) { toast.error('Enter a key name'); return; }
    setLoading(true);
    try {
      const data = await apiKeysApi.create(newKeyName.trim());
      setNewlyCreatedKey(data.key);
      setNewKeyName('');
      fetchApiKeys();
      toast.success('API key created');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create key');
    } finally { setLoading(false); }
  };

  const handleRevokeKey = async (keyId) => {
    try {
      await apiKeysApi.revoke(keyId);
      setApiKeys(prev => prev.filter(k => k.id !== keyId));
      toast.success('API key revoked');
    } catch { toast.error('Failed to revoke key'); }
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied');
  };

  if (!isPaid) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <Crown className="w-12 h-12 mx-auto text-amber-500" />
          <h2 className="text-xl font-semibold">API Access requires a Pro plan</h2>
          <Button onClick={() => navigate('/upgrade')}>Upgrade to Pro</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="api-page">
      {/* Header */}
      <div className="bg-white border-b border-zinc-200 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <Key className="w-5 h-5 text-zinc-700" />
          <h1 className="text-lg font-semibold">Developers</h1>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        <Tabs defaultValue="keys">
          <TabsList className="mb-6">
            <TabsTrigger value="keys">
              <Key className="w-4 h-4 mr-2" />
              Keys
            </TabsTrigger>
            <TabsTrigger value="docs">
              <BookOpen className="w-4 h-4 mr-2" />
              Documentation
            </TabsTrigger>
            <TabsTrigger value="usage">
              <BarChart3 className="w-4 h-4 mr-2" />
              Usage
            </TabsTrigger>
            <TabsTrigger value="webhooks">
              <Webhook className="w-4 h-4 mr-2" />
              Webhooks
            </TabsTrigger>
            <TabsTrigger value="mcp" data-testid="developer-mcp-tab">
              <Sparkles className="w-4 h-4 mr-2" />
              MCP
            </TabsTrigger>
          </TabsList>

          {/* ─── KEYS TAB ─── */}
          <TabsContent value="keys">
            <Card>
              <CardHeader>
                <CardTitle>API Keys</CardTitle>
                <CardDescription>Create and manage API keys for programmatic access.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Create */}
                <div className="flex gap-2">
                  <Input
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    placeholder="Key name (e.g. Production, CI/CD)"
                    className="flex-1"
                    onKeyDown={(e) => e.key === 'Enter' && handleCreateKey()}
                    data-testid="api-key-name-input"
                  />
                  <Button onClick={handleCreateKey} disabled={loading} data-testid="create-api-key-btn">
                    {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Create Key
                  </Button>
                </div>

                {/* Newly created */}
                {newlyCreatedKey && (
                  <div className="p-4 border border-emerald-200 bg-emerald-50 rounded-lg space-y-2">
                    <p className="text-sm font-medium text-emerald-800">Copy your key now — it won't be shown again.</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-white px-3 py-2 rounded border text-sm font-mono break-all" data-testid="new-api-key-value">
                        {showKey ? newlyCreatedKey : '\u2022'.repeat(40)}
                      </code>
                      <Button variant="ghost" size="sm" onClick={() => setShowKey(!showKey)}>
                        {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => copyText(newlyCreatedKey)} data-testid="copy-api-key-btn">
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Key list */}
                {loading && !apiKeys.length ? (
                  <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>
                ) : apiKeys.length === 0 ? (
                  <p className="text-sm text-zinc-500 py-4">No API keys yet.</p>
                ) : (
                  <div className="space-y-2">
                    {apiKeys.map((k) => (
                      <div key={k.id} className="flex items-center justify-between p-3 border border-zinc-200 rounded-lg" data-testid={`api-key-${k.id}`}>
                        <div className="space-y-0.5">
                          <p className="text-sm font-medium">{k.name}</p>
                          <p className="text-xs text-zinc-500 font-mono">{k.prefix}</p>
                          <p className="text-xs text-zinc-400">
                            Created {new Date(k.created_at).toLocaleDateString()}
                            {k.last_used_at && ` \u00b7 Last used ${new Date(k.last_used_at).toLocaleDateString()}`}
                          </p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleRevokeKey(k.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`revoke-key-${k.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── DOCS TAB ─── */}
          <TabsContent value="docs">
            <Card>
              <CardHeader>
                <CardTitle>API Documentation</CardTitle>
                <CardDescription>All requests require a Bearer token. Base URL: <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded">{API_BASE}</code></CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Quick start */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Quick Start</Label>
                  <div className="bg-zinc-900 text-zinc-100 rounded-lg p-4 text-sm font-mono relative group">
                    <button onClick={() => copyText(`curl -H "Authorization: Bearer YOUR_API_KEY" \\\n  ${API_BASE}/v1/forms`)} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded bg-zinc-700 hover:bg-zinc-600">
                      <Copy className="w-3.5 h-3.5 text-zinc-300" />
                    </button>
                    <pre className="whitespace-pre-wrap">{`curl -H "Authorization: Bearer YOUR_API_KEY" \\
  ${API_BASE}/v1/forms`}</pre>
                  </div>
                </div>

                {/* Auth */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Authentication</Label>
                  <p className="text-sm text-zinc-600">
                    Include your API key in the <code className="text-xs bg-zinc-100 px-1 rounded">Authorization</code> header:
                  </p>
                  <div className="bg-zinc-50 border rounded-lg p-3 text-sm font-mono">
                    Authorization: Bearer ff_live_...
                  </div>
                </div>

                {/* Response format */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Response Format</Label>
                  <p className="text-sm text-zinc-600">All responses are JSON. Errors return a <code className="text-xs bg-zinc-100 px-1 rounded">detail</code> field with a human-readable message.</p>
                  <div className="bg-zinc-900 text-zinc-100 rounded-lg p-4 text-sm font-mono">
                    <pre>{`// Success
{ "id": "abc-123", "title": "My Form", ... }

// Error
{ "detail": "Form not found" }`}</pre>
                  </div>
                </div>

                {/* Endpoints table */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Endpoints ({ENDPOINTS.length})</Label>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-zinc-50 border-b">
                          <th className="text-left px-4 py-2 font-medium text-zinc-600 w-20">Method</th>
                          <th className="text-left px-4 py-2 font-medium text-zinc-600">Path</th>
                          <th className="text-left px-4 py-2 font-medium text-zinc-600">Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ENDPOINTS.map((ep, i) => (
                          <tr key={i} className="border-b last:border-0 hover:bg-zinc-50">
                            <td className="px-4 py-2">
                              <span className={`text-xs font-mono font-medium px-1.5 py-0.5 rounded ${METHOD_COLORS[ep.method]}`}>{ep.method}</span>
                            </td>
                            <td className="px-4 py-2 font-mono text-xs text-zinc-700">{ep.path}</td>
                            <td className="px-4 py-2 text-zinc-600">{ep.desc}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Rate limits */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Rate Limits</Label>
                  <p className="text-sm text-zinc-600">API requests are limited to <strong>1,000 requests per hour</strong> per API key. Exceeding this limit will return a <code className="text-xs bg-zinc-100 px-1 rounded">429</code> status code.</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── USAGE TAB ─── */}
          <TabsContent value="usage">
            <Card>
              <CardHeader>
                <CardTitle>API Usage</CardTitle>
                <CardDescription>Monitor your API key activity.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Per-key usage */}
                {apiKeys.length === 0 ? (
                  <p className="text-sm text-zinc-500 py-4">Create an API key to start tracking usage.</p>
                ) : (
                  <div className="space-y-4">
                    {apiKeys.map((k) => (
                      <div key={k.id} className="border border-zinc-200 rounded-lg p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm">{k.name}</p>
                            <p className="text-xs text-zinc-500 font-mono">{k.prefix}</p>
                          </div>
                          <div className="flex items-center gap-1.5 text-xs text-zinc-500">
                            <Activity className="w-3.5 h-3.5" />
                            {k.last_used_at ? 'Active' : 'Never used'}
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="bg-zinc-50 rounded-lg p-3 text-center">
                            <p className="text-xs text-zinc-500">Created</p>
                            <p className="text-sm font-medium">{new Date(k.created_at).toLocaleDateString()}</p>
                          </div>
                          <div className="bg-zinc-50 rounded-lg p-3 text-center">
                            <p className="text-xs text-zinc-500">Last Used</p>
                            <p className="text-sm font-medium">
                              {k.last_used_at ? new Date(k.last_used_at).toLocaleDateString() : '--'}
                            </p>
                          </div>
                          <div className="bg-zinc-50 rounded-lg p-3 text-center">
                            <p className="text-xs text-zinc-500">Status</p>
                            <p className="text-sm font-medium">
                              <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${k.last_used_at ? 'bg-emerald-500' : 'bg-zinc-300'}`} />
                              {k.last_used_at ? 'Active' : 'Idle'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Summary */}
                <div className="border-t pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-zinc-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-zinc-500 mb-1">
                        <Key className="w-4 h-4" />
                        <p className="text-xs">Total Keys</p>
                      </div>
                      <p className="text-2xl font-semibold">{apiKeys.length}<span className="text-sm text-zinc-400 font-normal"> / 5</span></p>
                    </div>
                    <div className="bg-zinc-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-zinc-500 mb-1">
                        <Clock className="w-4 h-4" />
                        <p className="text-xs">Last Activity</p>
                      </div>
                      <p className="text-2xl font-semibold">
                        {apiKeys.some(k => k.last_used_at)
                          ? new Date(Math.max(...apiKeys.filter(k => k.last_used_at).map(k => new Date(k.last_used_at)))).toLocaleDateString()
                          : '--'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── WEBHOOKS TAB ─── */}
          <TabsContent value="webhooks">
            <Card>
              <CardHeader>
                <CardTitle>Webhooks</CardTitle>
                <CardDescription>Receive real-time HTTP POST notifications when events happen in your workspace.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* How it works */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">How It Works</Label>
                  <p className="text-sm text-zinc-600">
                    Register a URL via the API and subscribe to one or more events below. When an event fires, we send a <code className="text-xs bg-zinc-100 px-1 rounded">POST</code> request to your endpoint with a JSON payload containing the event type, timestamp, and relevant data.
                  </p>
                </div>

                {/* Setup example */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Register a Webhook</Label>
                  <div className="bg-zinc-900 text-zinc-100 rounded-lg p-4 text-sm font-mono relative group">
                    <button onClick={() => copyText(`curl -X POST ${API_BASE}/v1/forms/:id/webhooks \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "url": "https://your-server.com/webhook",
    "events": ["submission.created", "form.published"],
    "secret": "whsec_your_signing_secret"
  }'`)} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded bg-zinc-700 hover:bg-zinc-600">
                      <Copy className="w-3.5 h-3.5 text-zinc-300" />
                    </button>
                    <pre className="whitespace-pre-wrap">{`curl -X POST ${API_BASE}/v1/forms/:id/webhooks \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "url": "https://your-server.com/webhook",
    "events": ["submission.created", "form.published"],
    "secret": "whsec_your_signing_secret"
  }'`}</pre>
                  </div>
                </div>

                {/* Payload example */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Payload Format</Label>
                  <p className="text-sm text-zinc-600">Every webhook delivery includes a signature in the <code className="text-xs bg-zinc-100 px-1 rounded">X-FF-Signature</code> header so you can verify authenticity.</p>
                  <div className="bg-zinc-900 text-zinc-100 rounded-lg p-4 text-sm font-mono relative group">
                    <button onClick={() => copyText(`{
  "event": "submission.created",
  "timestamp": "2026-04-04T12:00:00Z",
  "form_id": "abc-123",
  "data": {
    "submission_id": "sub_456",
    "fields": { "email": "user@example.com", ... }
  }
}`)} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded bg-zinc-700 hover:bg-zinc-600">
                      <Copy className="w-3.5 h-3.5 text-zinc-300" />
                    </button>
                    <pre className="whitespace-pre-wrap">{`{
  "event": "submission.created",
  "timestamp": "2026-04-04T12:00:00Z",
  "form_id": "abc-123",
  "data": {
    "submission_id": "sub_456",
    "fields": { "email": "user@example.com", ... }
  }
}`}</pre>
                  </div>
                </div>

                {/* Events table */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Available Events ({WEBHOOK_EVENTS.length})</Label>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-zinc-50 border-b">
                          <th className="text-left px-4 py-2.5 font-medium text-zinc-600 w-64">Event</th>
                          <th className="text-left px-4 py-2.5 font-medium text-zinc-600">Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {WEBHOOK_EVENTS.map((wh, i) => (
                          <tr key={i} className="border-b last:border-0 hover:bg-zinc-50" data-testid={`webhook-event-${wh.event}`}>
                            <td className="px-4 py-2.5">
                              <code className="text-xs font-mono bg-zinc-100 px-2 py-1 rounded text-zinc-800">{wh.event}</code>
                            </td>
                            <td className="px-4 py-2.5 text-zinc-600">{wh.desc}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Retry policy */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Retry Policy</Label>
                  <p className="text-sm text-zinc-600">
                    If your endpoint returns a non-<code className="text-xs bg-zinc-100 px-1 rounded">2xx</code> status code, we retry up to <strong>5 times</strong> with exponential backoff (1 min, 5 min, 30 min, 2 hrs, 24 hrs). After all retries fail, a <code className="text-xs bg-zinc-100 px-1 rounded">webhook.failed</code> event is emitted.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ─── MCP TAB ─── */}
          <TabsContent value="mcp">
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      Model Context Protocol
                      <span className="text-xs font-medium bg-zinc-100 text-zinc-700 px-2 py-0.5 rounded-full">Pro</span>
                    </CardTitle>
                    <CardDescription>
                      Connect Claude Desktop, Cursor, or any MCP-aware AI assistant to your Fantastic Forms workspace. Read forms / submissions / analytics, plus build entire forms via natural language — all guarded by your Pro API key. Destructive operations (delete form, delete submission) are intentionally excluded.
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Endpoint URL */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Endpoint URL</Label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-sm bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 font-mono text-zinc-900 break-all" data-testid="mcp-endpoint-url">
                      {API_BASE}/mcp
                    </code>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(`${API_BASE}/mcp`);
                        toast.success('Endpoint URL copied');
                      }}
                      data-testid="mcp-copy-endpoint"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>

                {/* Authentication */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Authentication</Label>
                  <p className="text-sm text-zinc-600">
                    Send your API key (from the <strong>Keys</strong> tab) as a Bearer token. The MCP endpoint accepts the same <code className="text-xs bg-zinc-100 px-1 rounded">ff_live_…</code> keys you use for the REST API.
                  </p>
                </div>

                {/* Available tools */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Available tools</Label>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-zinc-500 mb-2">Read</p>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {[
                        { name: 'list_forms', desc: 'Discover form IDs in the workspace.' },
                        { name: 'get_form', desc: 'Inspect a form\'s structure & logic.' },
                        { name: 'list_submissions', desc: 'Read submission answers + metadata.' },
                        { name: 'get_analytics', desc: 'Aggregated metrics over a date range.' },
                      ].map((t) => (
                        <div key={t.name} className="border border-zinc-200 rounded-lg p-3">
                          <code className="text-xs font-mono font-semibold text-zinc-900">{t.name}</code>
                          <p className="text-xs text-zinc-600 mt-1">{t.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-zinc-500 mb-2 mt-3">Write</p>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {[
                        { name: 'create_form', desc: 'Create a form (optionally with seed fields).' },
                        { name: 'update_form', desc: 'Patch title, description, settings, branding.' },
                        { name: 'publish_form', desc: 'Set status to published.' },
                        { name: 'unpublish_form', desc: 'Take a form offline (status → draft).' },
                        { name: 'add_field', desc: 'Append a new field/element.' },
                        { name: 'update_field', desc: 'Patch an existing field.' },
                        { name: 'delete_field', desc: 'Remove a single field from a form.' },
                        { name: 'add_logic_rule', desc: 'Add conditional show/hide/jump rules.' },
                      ].map((t) => (
                        <div key={t.name} className="border border-zinc-200 rounded-lg p-3">
                          <code className="text-xs font-mono font-semibold text-zinc-900">{t.name}</code>
                          <p className="text-xs text-zinc-600 mt-1">{t.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="text-[11px] text-zinc-500 italic mt-2">
                    Note: <code className="bg-zinc-100 px-1 rounded">delete_form</code> and <code className="bg-zinc-100 px-1 rounded">delete_submission</code> are intentionally NOT exposed — a leaked key can't wipe data wholesale. Field-level delete is included because iteration is core to form-building.
                  </p>
                </div>

                {/* Claude Desktop setup */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Connect from Claude Desktop</Label>
                  <p className="text-sm text-zinc-600">
                    Add this to your <code className="text-xs bg-zinc-100 px-1 rounded">claude_desktop_config.json</code> (Settings → Developer → Edit Config), then restart Claude Desktop:
                  </p>
                  <pre className="bg-zinc-900 text-zinc-100 text-xs rounded-lg p-4 overflow-x-auto font-mono leading-relaxed">
{`{
  "mcpServers": {
    "fantastic-forms": {
      "command": "npx",
      "args": [
        "-y", "mcp-remote",
        "${API_BASE}/mcp",
        "--header",
        "Authorization: Bearer ff_live_YOUR_KEY_HERE"
      ]
    }
  }
}`}
                  </pre>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const snippet = `{
  "mcpServers": {
    "fantastic-forms": {
      "command": "npx",
      "args": [
        "-y", "mcp-remote",
        "${API_BASE}/mcp",
        "--header",
        "Authorization: Bearer ff_live_YOUR_KEY_HERE"
      ]
    }
  }
}`;
                      navigator.clipboard.writeText(snippet);
                      toast.success('Config snippet copied');
                    }}
                    data-testid="mcp-copy-config"
                  >
                    <Copy className="w-3.5 h-3.5 mr-2" />
                    Copy config
                  </Button>
                  <p className="text-xs text-zinc-500 mt-2">
                    Replace <code className="text-xs bg-zinc-100 px-1 rounded">ff_live_YOUR_KEY_HERE</code> with an API key from the Keys tab. <code className="text-xs bg-zinc-100 px-1 rounded">mcp-remote</code> bridges the remote endpoint to Claude Desktop's local stdio transport — no extra install needed beyond npx.
                  </p>
                </div>

                {/* Cursor / other clients */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Cursor, Continue, and other clients</Label>
                  <p className="text-sm text-zinc-600">
                    Any client that supports remote Streamable-HTTP MCP can connect directly. Point it at <code className="text-xs bg-zinc-100 px-1 rounded">{`${API_BASE}/mcp`}</code> with an <code className="text-xs bg-zinc-100 px-1 rounded">Authorization: Bearer</code> header — no bridge required.
                  </p>
                </div>

                {/* Example prompts */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Try asking your AI</Label>
                  <ul className="text-sm text-zinc-600 space-y-1.5 list-disc pl-5">
                    <li>"Summarize the last 50 responses to my contact form."</li>
                    <li>"What's the completion rate on my onboarding survey over the last 7 days?"</li>
                    <li>"Audit my pricing form's logic rules and tell me if anything looks unreachable."</li>
                    <li>"Build me an NPS survey: a 0–10 linear scale and a follow-up long-text field. Then publish it."</li>
                    <li>"Add an email field to my newsletter signup form and make it required."</li>
                    <li>"Hide the 'company size' field unless the user picks 'Business' on the plan question."</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
